import React from 'react'

import { BlockContainer } from '@Components/BlockContainer'
import { Link } from '@Components/Link'
import { Tiles } from '@Components/Tiles'
import { TilesCardLook } from '@Components/Tiles/Card/Look'
import { getAutomaticV2TileSettings, useTileSettings } from '@Components/Tiles/settings'
import { calculateSizes } from '@Components/Tiles/sizes'
import { BlockAppearance, BlockCommon, LookTile, Maybe, PagePresentation, PageType, Scalars } from '@Generated/graphql'
import { getPins } from '@Globals/CMS/LookHelpers'
import { useCMSModalOpen } from '@Globals/CMS/Modal/Hook'
import { Image } from '@Globals/Image'
import { useTranslation } from '@Globals/Translations'

import styles from './Looks.module.scss'

export const Looks = ({
  appearance,
  common,
  id,
  looks,
  noPadding = false,
  presentation,
  settings,
  title,
  titleOverride,
}: {
  appearance?: Maybe<BlockAppearance>
  common?: Maybe<BlockCommon>
  id?: string
  looks: LookTile[]
  noPadding?: boolean
  presentation?: PagePresentation
  settings?: Maybe<Scalars['JSON']>
  title?: string
  titleOverride?: React.ReactNode
}) => {
  const parsedSettings = useTileSettings(settings)
  const openModal = useCMSModalOpen()
  const { t } = useTranslation()

  if (!looks || looks.length === 0) {
    return null
  }

  // Looks render v2 tiles unconditionally: the authored version is ignored and
  // v1-authored fields fall back to the v2 defaults. Normal tiles blocks still support
  // both versions through useTileSettings.
  const baseSettings = { ...parsedSettings, version: '2' }

  // `automatic` opts into count-based settings, like v1's hardcoded look lists
  // (getAutomaticTileSettings in graph-api) — but per block instead of always
  const tileSettings = baseSettings.automatic
    ? { ...baseSettings, ...getAutomaticV2TileSettings(looks.length) }
    : baseSettings

  // the single-look presentation (common beside the tile, neutral background), governed
  // by the `single_look` setting (default on) so faceted blocks can opt out
  const singleLookPresentation = looks.length === 1 && tileSettings.singleLook
  // BlockContainer only renders the common column when there is a title to show
  const hasCommonContent = Boolean(titleOverride || title || common?.title)
  let commonPosition: string | null | undefined
  if (hasCommonContent) {
    commonPosition = singleLookPresentation ? 'left' : common?.position
  }

  const sizesHint = calculateSizes(tileSettings, looks.length, {
    commonPosition,
    commonWidth: hasCommonContent && singleLookPresentation ? 'half' : undefined,
    noPadding,
    presentation,
  })

  return (
    <BlockContainer
      appearance={{
        ...appearance || {},
        backgroundColor: singleLookPresentation ? 'var(--neutral-100)' : appearance?.backgroundColor,
      }}
      className={styles.blockContainer}
      common={{
        ...common,
        text: singleLookPresentation
          ? {
            key: 'common:discover_get_the_look',
            value: {
              text: t('common:discover_get_the_look'),
              type: 'p',
            },
          }
          : (common?.text || undefined),
        title: title || common?.title || '',
        titleElement: common?.titleElement || 'h2',
        position: singleLookPresentation ? 'left' : (common?.position || 'top'),
      }}
      commonWidth={singleLookPresentation ? 'half' : undefined}
      id={id}
      noPadding={noPadding}
      presentation={presentation || PagePresentation.Full}
      titleOverride={titleOverride}
    >
      <Tiles settings={tileSettings}>
        {looks.map(look => (
          <TilesCardLook
            key={look.id}
            ambassadorHandle={look.ambassadorHandle || undefined}
            cardAspectRatio={tileSettings.aspectRatio}
            image={look.asset as Image}
            onClick={tileSettings.action === 'drawer' || !look.url
              ? (e) => {
                e.preventDefault()
                e.stopPropagation()

                openModal(PageType.Look, look.id!, 'inherit', presentation || PagePresentation.Sidebar)
              }
              : undefined}
            pins={getPins(look, undefined, presentation || PagePresentation.Sidebar)}
            presentation={presentation || PagePresentation.Sidebar}
            render={tileSettings.action === 'page' && look.url
              ? props => (
                <Link href={look.url!} {...props} />
              )
              : undefined}
            renderButtonAsText={Boolean(tileSettings.action === 'page' && look.url)}
            sizes={sizesHint}
          />
        ))}
      </Tiles>
    </BlockContainer>
  )
}
